// Web push do painel via FCM (Firebase Cloud Messaging), convivendo com o OneSignal
// (ADR-0002, docs/push-web-fcm.md).
//
// Registro por Firebase Installation ID (FID), o caminho atual da documentação do Firebase JS (12.14+):
// register() + onRegistered() entregam o FID, que vai ao backend (o REST v1 envia com `message.fid`);
// getToken/deleteToken estão obsoletos. unregister() + onUnregistered() desfazem o registro.
//
// Quem decide o canal é o backend (GET /admin/me/push-config -> fcmEnabled). Só vira FCM
// quando o backend liga o envio por FCM E a config do app web do Firebase está no build E o
// navegador suporta; em qualquer outro caso (inclusive erro na consulta) o fluxo é o do
// OneSignal, exatamente como antes (notificationService.registerAdmin).
//
// Nunca lança: falha de push não pode afetar login/logout nem a navegação do painel.

import axios from 'axios'
import api from './apiService'
import { registerAdmin, unregisterAdmin } from './notificationService'
import { resolveWebNotificationRoute } from '../notifications/resolveWebNotificationRoute'

const FID_KEY = 'painel.push.fid'
// Token FCM legado (versão anterior deste serviço, getToken): ao registrar o FID o backend é avisado para
// removê-lo, como a documentação orienta ("remover qualquer token legado desta instância").
const LEGACY_TOKEN_KEY = 'painel.push.fcmToken'
// Quando o navegador foi perguntado pela última vez e o usuário dispensou (permissão segue 'default'):
// não insistimos antes de PROMPT_BACKOFF_MS.
const PROMPTED_KEY = 'painel.push.promptedAt'
const PROMPT_BACKOFF_MS = 7 * 24 * 3600 * 1000
// Marca que ESTE navegador foi desinscrito do OneSignal por causa do FCM (evita push duplicado);
// ao voltar para o OneSignal, a marca diz que é preciso reinscrever.
const OS_OPTOUT_KEY = 'painel.push.onesignalOptedOut'
// Escopo próprio: o OneSignalSDKWorker.js ocupa o escopo "/" e dois workers não dividem escopo.
const SW_SCOPE = '/firebase-cloud-messaging-push-scope'

let _foregroundAttached = false
let _clickAttached = false
// Evita repetir o POST do mesmo FID/permissão (registerWebPush roda no mount e no watch de auth).
let _lastPosted = null
let _messaging = null
let _registeredHandling = null   // promessa do tratamento do último onRegistered (register() não devolve o FID)
let _gestureArmed = false

function lsGet(key) {
  try { return localStorage.getItem(key) } catch { return null }
}
function lsSet(key, value) {
  try { localStorage.setItem(key, value) } catch { /* storage bloqueado */ }
}
function lsRemove(key) {
  try { localStorage.removeItem(key) } catch { /* storage bloqueado */ }
}

function readBearer() {
  try {
    return JSON.parse(localStorage.getItem('user') ?? 'null')?.bearer_token ?? null
  } catch {
    return null
  }
}

/**
 * Config do app WEB do Firebase (variáveis do build). Devolve null se faltar algo obrigatório (apiKey, projectId,
 * messagingSenderId, appId; a chave VAPID é opcional):
 * sem ela o painel nem tenta o FCM e segue no OneSignal. A apiKey web não é segredo.
 */
export function getFirebaseWebConfig() {
  const env = process.env
  const cfg = {
    apiKey:            env.VUE_APP_FIREBASE_API_KEY || '',
    authDomain:        env.VUE_APP_FIREBASE_AUTH_DOMAIN || '',
    projectId:         env.VUE_APP_FIREBASE_PROJECT_ID || '',
    messagingSenderId: env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID || '',
    appId:             env.VUE_APP_FIREBASE_APP_ID || '',
  }
  // A chave VAPID é OPCIONAL no registro por FID (RegisterOptions.vapidKey): sem ela o SDK usa a chave padrão
  // do Firebase. Só passamos a nossa se estiver configurada.
  const vapidKey = env.VUE_APP_FIREBASE_VAPID_KEY || ''
  if (!cfg.apiKey || !cfg.projectId || !cfg.messagingSenderId || !cfg.appId) return null
  return { ...cfg, vapidKey }
}

function mapPermission(permission) {
  if (permission === 'granted') return 'granted'
  if (permission === 'denied') return 'denied'
  return 'not_asked'
}

async function isFcmSupported() {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false
  if (!('serviceWorker' in navigator) || !('Notification' in window) || !('PushManager' in window)) return false
  try {
    const { isSupported } = await import('firebase/messaging')
    return await isSupported()
  } catch {
    return false
  }
}

/** O backend está com o envio por FCM ativado? Qualquer erro vale "não" (cai no OneSignal). */
export async function fetchFcmEnabled() {
  try {
    const { data } = await api.get('/admin/me/push-config')
    return data?.fcmEnabled === true
  } catch {
    return false
  }
}

/** 'fcm' só com config do Firebase no build + navegador suportado + backend ativado; senão 'onesignal'. */
export async function resolveWebPushProvider() {
  if (!getFirebaseWebConfig()) return 'onesignal'
  if (!(await isFcmSupported())) return 'onesignal'
  return (await fetchFcmEnabled()) ? 'fcm' : 'onesignal'
}

function serviceWorkerUrl(config) {
  const q = new URLSearchParams({
    apiKey:            config.apiKey,
    authDomain:        config.authDomain,
    projectId:         config.projectId,
    messagingSenderId: config.messagingSenderId,
    appId:             config.appId,
  })
  return `/firebase-messaging-sw.js?${q.toString()}`
}

async function postDevice(fid, permissionStatus) {
  const key = `${fid}|${permissionStatus}`
  if (key === _lastPosted) return
  const appVersion = process.env.VUE_APP_VERSION || undefined
  await api.post('/admin/me/push-device', {
    pushToken: fid, tokenKind: 'fid', platform: 'web', provider: 'fcm', permissionStatus,
    ...(appVersion ? { appVersion } : {}),
  })
  _lastPosted = key
}

// Logout/troca de canal: o bearer vem de fora porque, no logout, a sessão já foi limpa quando a
// requisição sai. axios puro (sem os interceptors do apiService): um 401 aqui não pode virar redirect
// nem toast, é só limpeza best-effort.
async function deleteDevice(pushToken, bearer) {
  const base = process.env.VUE_APP_API_URL || 'http://localhost:3000'
  await axios.delete(`${base}/admin/me/push-device`, {
    data:    { pushToken },
    headers: { Authorization: `Bearer ${bearer}`, 'x-app-name': 'painel-adm' },
  })
}

// Notificação em foreground: o FCM não exibe nada com a aba aberta. Mostra pelo service worker
// (assim o clique cai no mesmo notificationclick do background) e avisa o painel (evento).
function showForeground(registration, payload) {
  const data = payload?.data ?? {}
  try {
    window.dispatchEvent(new CustomEvent('painel:push', { detail: data }))
  } catch { /* ambiente sem CustomEvent */ }

  const title = payload?.notification?.title ?? data.title
  const body  = payload?.notification?.body ?? data.body
  if (!title && !body) return
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return
  registration.showNotification(title ?? 'Painel Taualegre', { body, tag: data.push_id, data })
}

async function optOutOneSignal(OneSignal) {
  if (!OneSignal || lsGet(OS_OPTOUT_KEY)) return
  try {
    await OneSignal.User.PushSubscription.optOut()
    OneSignal.logout()
    lsSet(OS_OPTOUT_KEY, '1')
  } catch { /* SDK indisponível: sem duplicidade a evitar */ }
}

async function optInOneSignal(OneSignal) {
  if (!lsGet(OS_OPTOUT_KEY)) return
  try {
    if (OneSignal) await OneSignal.User.PushSubscription.optIn()
    lsRemove(OS_OPTOUT_KEY)
  } catch { /* tenta de novo na próxima carga */ }
}

// FID recebido (onRegistered): remove token legado/FID anterior no backend e registra o FID atual.
async function handleRegistered(fid) {
  const bearer = readBearer()
  const stale = [lsGet(LEGACY_TOKEN_KEY), lsGet(FID_KEY)].filter((v) => v && v !== fid)
  for (const old of stale) {
    if (bearer) await deleteDevice(old, bearer).catch(() => { /* o backend remove id inválido sozinho */ })
  }
  lsRemove(LEGACY_TOKEN_KEY)
  lsSet(FID_KEY, fid)
  await postDevice(fid, 'granted')
}

// unregister() concluído (ou registro derrubado pelo SDK): o FID não vale mais — o backend precisa saber, senão
// o próximo envio falha com 404.
async function handleUnregistered(fid) {
  if (lsGet(FID_KEY) !== fid) return // já tratado (logout fez o DELETE antes de desregistrar)
  const bearer = readBearer()
  if (!bearer) return // sem sessão não dá para autenticar: o próximo login reatribui
  try {
    await deleteDevice(fid, bearer)
    lsRemove(FID_KEY)
    _lastPosted = null
  } catch { /* best-effort */ }
}

// Registra este navegador no FCM por FID (a permissão já tem que estar 'granted') e no backend.
// Devolve o FID, ou null se não foi possível (erro de SW/SDK).
async function registerFcm(config) {
  const [{ initializeApp, getApps, getApp }, { getMessaging, register, onRegistered, onUnregistered, onMessage }] = await Promise.all([
    import('firebase/app'),
    import('firebase/messaging'),
  ])
  const app = getApps().length ? getApp() : initializeApp({
    apiKey: config.apiKey, authDomain: config.authDomain, projectId: config.projectId,
    messagingSenderId: config.messagingSenderId, appId: config.appId,
  })
  const registration = await navigator.serviceWorker.register(serviceWorkerUrl(config), { scope: SW_SCOPE })
  const messaging = getMessaging(app)

  // Os handlers precisam existir ANTES do register() (o SDK recusa register sem onRegistered). onRegistered
  // dispara a cada register() e quando o SDK renova o registro; guardamos a promessa para aguardar o POST.
  if (messaging !== _messaging) {
    _messaging = messaging
    onRegistered(messaging, (fid) => { _registeredHandling = handleRegistered(fid) })
    onUnregistered(messaging, (fid) => { handleUnregistered(fid) })
  }
  if (!_foregroundAttached) {
    _foregroundAttached = true
    onMessage(messaging, (payload) => showForeground(registration, payload))
  }

  _registeredHandling = null
  await register(messaging, {
    ...(config.vapidKey ? { vapidKey: config.vapidKey } : {}),
    serviceWorkerRegistration: registration,
  })
  await _registeredHandling
  return lsGet(FID_KEY)
}

// Mudança de permissão nas configurações do navegador não gera evento no Notification: observa pela
// Permissions API e refaz o registro (FID novo, ou aviso ao backend de que foi negado).
let _permissionWatched = false
function watchPermission(onChange) {
  if (_permissionWatched || typeof navigator === 'undefined' || !navigator.permissions?.query) return
  _permissionWatched = true
  navigator.permissions.query({ name: 'notifications' })
    .then((status) => { status.onchange = () => onChange() })
    .catch(() => { /* navegador sem suporte */ })
}

// Permissão ainda não decidida. Navegadores (Safari, Firefox) só mostram o pedido dentro de um gesto do
// usuário e o Chrome silencia pedidos sem gesto: em vez de pedir ao carregar, espera o primeiro clique/tecla
// em qualquer lugar do painel e chama requestPermission DE FORMA SÍNCRONA no handler (a ativação transitória
// do gesto vale só por poucos segundos e a rede no meio a perderia). Respeita o backoff depois de dispensado.
function armPermissionPrompt(onGranted) {
  if (_gestureArmed || typeof document === 'undefined') return
  const last = Number(lsGet(PROMPTED_KEY) || 0)
  if (last && Date.now() - last < PROMPT_BACKOFF_MS) return
  _gestureArmed = true
  const events = ['click', 'keydown']
  const handler = () => {
    events.forEach((e) => document.removeEventListener(e, handler, true))
    _gestureArmed = false
    lsSet(PROMPTED_KEY, String(Date.now()))
    Promise.resolve(Notification.requestPermission())
      .then((permission) => { if (permission === 'granted') onGranted() })
      .catch(() => { /* pedido bloqueado pelo navegador */ })
  }
  events.forEach((e) => document.addEventListener(e, handler, true))
}

/**
 * Registra o aparelho do admin no canal certo. Substitui registerAdmin nas chamadas do AppLayout.
 * @returns {Promise<'fcm'|'onesignal'>} canal efetivamente usado
 */
export async function registerWebPush(OneSignal, adminId) {
  try {
    const provider = await resolveWebPushProvider()
    if (provider === 'fcm') {
      const permission = Notification.permission
      if (permission === 'denied') {
        // Negado no navegador: não há o que registrar e insistir não adianta (nem o OneSignal poderia pedir).
        // Se havia um registro, o backend passa a saber que foi negado.
        const stored = lsGet(FID_KEY)
        if (stored) await postDevice(stored, 'denied').catch(() => {})
        return 'fcm'
      }
      if (permission === 'default') {
        armPermissionPrompt(() => registerWebPush(OneSignal, adminId))
        return 'fcm'
      }
      const fid = await registerFcm(getFirebaseWebConfig())
      if (fid) {
        await optOutOneSignal(OneSignal)
        watchPermission(() => registerWebPush(OneSignal, adminId))
        return 'fcm'
      }
      // FCM não funcionou neste navegador: o OneSignal continua recebendo (o backend envia pelos dois canais).
    }
  } catch {
    // erro de SW/SDK/rede: segue para o OneSignal
  }

  await dropFcmDevice()
  await optInOneSignal(OneSignal)
  await registerAdmin(OneSignal, adminId)
  return 'onesignal'
}

async function messagingOfExistingApp(getMessaging) {
  const { getApps, getApp } = await import('firebase/app')
  return getApps().length ? getMessaging(getApp()) : null
}

// Desfaz o registro FCM deste navegador: apaga o FID no backend e desregistra no SDK (unregister).
// Sem sessão não dá para autenticar o DELETE: o FID fica guardado e o próximo login reatribui.
async function dropFcmDevice(session) {
  const stored = lsGet(FID_KEY)
  const legacy = lsGet(LEGACY_TOKEN_KEY)
  if (!stored && !legacy) return
  const bearer = session?.bearer ?? readBearer()
  if (!bearer) return

  let deleted = true
  for (const id of [stored, legacy].filter(Boolean)) {
    try {
      await deleteDevice(id, bearer)
    } catch {
      deleted = false // o backend remove id inválido sozinho
    }
  }
  if (deleted) {
    lsRemove(FID_KEY)
    lsRemove(LEGACY_TOKEN_KEY)
    _lastPosted = null
  }
  try {
    const { unregister, getMessaging } = await import('firebase/messaging')
    const messaging = _messaging ?? await messagingOfExistingApp(getMessaging)
    if (messaging) await unregister(messaging)
  } catch { /* SDK indisponível ou nunca registrado nesta carga */ }
}

/**
 * Captura a sessão ANTES de limpá-la (auth.logout() zera o localStorage) para o DELETE do FID
 * poder se autenticar depois. Chamar no início do logout.
 */
export function captureWebPushSession() {
  return { bearer: readBearer() }
}

/** Logout: desvincula o FID da conta (best-effort) e desassocia o OneSignal como antes. */
export async function unregisterWebPush(OneSignal, session) {
  try {
    await dropFcmDevice(session)
  } catch { /* ignore */ }
  await unregisterAdmin(OneSignal)
}

function handleClick(data, router) {
  const route = resolveWebNotificationRoute(data)
  if (route) router.push(route)
}

/**
 * Clique em push FCM: o service worker manda `postMessage` para a aba aberta, ou abre o painel com
 * `?pushClick=<json>`. Mesma regra de destino do clique do OneSignal (resolveWebNotificationRoute).
 * Chamado uma vez do main.js.
 */
export function setupWebPushClickHandling(router) {
  if (_clickAttached || !router) return
  _clickAttached = true
  try {
    navigator.serviceWorker?.addEventListener('message', (event) => {
      if (event.data?.source === 'painel-push' && event.data.kind === 'click') handleClick(event.data.data, router)
    })
  } catch { /* sem service worker */ }

  try {
    const params = new URLSearchParams(window.location.search)
    const raw = params.get('pushClick')
    if (!raw) return
    params.delete('pushClick')
    const qs = params.toString()
    window.history.replaceState(null, '', `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`)
    const data = JSON.parse(raw)
    router.isReady().then(() => handleClick(data, router))
  } catch { /* query malformada: ignora */ }
}

/** Só para testes: zera o estado de módulo. */
export function __resetWebPushForTests() {
  _foregroundAttached = false
  _clickAttached = false
  _lastPosted = null
  _permissionWatched = false
  _messaging = null
  _registeredHandling = null
  _gestureArmed = false
}
